/**
 * Create a user, or edit one.
 *
 * ---------------------------------------------------------------------------
 * WHAT CHANGED, AND WHY
 *
 * The pages picker here was a FLAT `MultipleSelect` — the same rows as
 * «إعادة تعيين الصفحة», with the grouping thrown away — and it was rendered
 * only when creating. On edit, `mode` is `'Edit'`, so the `mode !== 'Edit'`
 * guard hid the picker AND dropped `pages` from the payload: an operator who
 * opened «تعديل» to change someone's access found nothing to change, and had
 * to close the form and reopen a different modal.
 *
 * Both now draw the same `PagesTree`, and both send `pages`. Create and update
 * agree with each other, and with the sidebar.
 *
 * The password field stays create-only. Changing a password is
 * «إعادة تعيين كلمة المرور», which posts to a different endpoint
 * (`ResetForcePassword`) — sending a blank one from here would not have been a
 * no-op.
 */

import React, { useEffect, useState } from 'react';
import Modal from '../../Atoms/Modal/Modal';
import ModalChildrenContainer from '../../Atoms/Modal/ModalChildrenContainer';
import TextInput from '../../Atoms/Inputs/TextInput';
import Buttons from '../../Atoms/Buttons/Buttons';
import usericon from '../../../assets/Imagess/userinputicon.svg';
import emailicon from '../../../assets/Imagess/emailinputicon.svg';
import mobile from '../../../assets/Imagess/mobile.svg';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useAddUser } from '../../../Hooks/Users';
import LoaderButton from '../Loader/LoaderButton';
import { useToast } from '../Custom/CustomToaster/Toast';
import PagesTree, {
  selectionFromUserRoles,
} from './RolesModal/PagesTree';

const EMPTY_USER_ID = '00000000-0000-0000-0000-000000000000';

const UserModal = ({
  handleOnClose,
  title,
  mode,
  refetchUsers,
  eachUser = null,
  pagesData,
}) => {
  // `mode` arrives as `'add'` from the toolbar and `'Edit'` from the row menu.
  // One derived flag, so a casing slip cannot silently change behaviour again —
  // which is exactly how the pages picker came to be hidden on edit.
  const isAdd = mode === 'add';

  const [errorMessage, setErrorMessage] = useState('');
  const { mutate: addUser, isLoading: isAddingUser } = useAddUser();
  const customizedToast = useToast();

  const [selectedPages, setSelectedPages] = useState(() => new Set());

  useEffect(() => {
    if (isAdd || !eachUser || !pagesData?.length) return;
    setSelectedPages(selectionFromUserRoles(eachUser.userRoles, pagesData));
  }, [isAdd, eachUser, pagesData]);

  const initialValues = {
    fullNameEn: isAdd ? '' : eachUser?.fullName_En ?? '',
    fullNameAr: isAdd ? '' : eachUser?.fullName_AR ?? '',
    email: isAdd ? '' : eachUser?.email ?? '',
    mobile: isAdd ? '' : eachUser?.mobile ?? '',
    username: isAdd ? '' : eachUser?.userName ?? '',
    password: '',
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required('البريد الإلكتروني مطلوب')
      .email('البريد الإلكتروني غير صحيح'),
    fullNameAr: Yup.string().required('الاسم بالعربية مطلوب'),
    fullNameEn: Yup.string().required('الاسم بالإنجليزية مطلوب'),
    username: Yup.string().required('اسم المستخدم مطلوب'),
    mobile: Yup.string().required('رقم الهاتف مطلوب'),
    password: isAdd
      ? Yup.string()
          .required('كلمة المرور مطلوبة')
          .matches(
            /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()])[A-Za-z\d!@#$%^&*()]{8,15}$/,
            'يجب أن تحتوي كلمة المرور على 8 إلى 15 حرفًا، بما في ذلك حرف واحد كبير، حرف واحد صغير، رقم واحد، ورمز خاص واحد.'
          )
      : Yup.string().notRequired(),
    // NOTE: the pages are intentionally NOT validated. Creating an account with
    // no pages is a legitimate thing to do — the user signs in and meets the
    // no-permission screen until someone grants them something.
  });

  const handleSubmit = (values) => {
    const payload = {
      userId: eachUser?.userId || EMPTY_USER_ID,
      fullNameEn: values.fullNameEn,
      fullNameAr: values.fullNameAr,
      email: values.email,
      mobile: values.mobile,
      username: values.username,
      pages: Array.from(selectedPages),
    };

    if (isAdd) payload.password = values.password;

    addUser(payload, {
      onSuccess: () => {
        refetchUsers();
        handleOnClose();
        customizedToast.open(isAdd ? 'تم إضافة المستخدم' : 'تم تحديث المستخدم');
      },
      onError: (error) => {
        setErrorMessage(
          error?.response?.data?.error ||
            error?.response?.data?.message ||
            'البريد الإلكتروني أو اسم المستخدم موجود بالفعل'
        );
      },
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, setFieldValue, handleSubmit: submit }) => (
        <Form>
          <Modal
            onClose={handleOnClose}
            title={`${isAdd ? 'إضافة' : 'تعديل'} ${title}`}
            width='44rem'
            height='92vh'
            scrollY='auto'
            closeWhite={false}
            modalInnerContainerStyle={{ maxHeight: '100vh' }}
          >
            <ModalChildrenContainer>
              <TextInput
                label='الاسم الأول'
                icon={usericon}
                placeholder='أدخل الاسم الأول'
                name='fullNameEn'
                value={values.fullNameEn}
                onChange={(name, value) => setFieldValue(name, value)}
                errors={errors.fullNameEn}
                touched={touched.fullNameEn}
                required
                left
              />

              <TextInput
                label='الاسم الأخير'
                icon={usericon}
                placeholder='أدخل الاسم الأخير'
                name='fullNameAr'
                value={values.fullNameAr}
                onChange={(name, value) => setFieldValue(name, value)}
                errors={errors.fullNameAr}
                touched={touched.fullNameAr}
                required
                left
              />

              <TextInput
                label='البريد الإلكتروني'
                icon={emailicon}
                placeholder='أدخل البريد الإلكتروني'
                name='email'
                value={values.email}
                onChange={(name, value) => setFieldValue(name, value)}
                errors={errors.email || errorMessage}
                touched={touched.email || errorMessage}
                required
                left
              />

              <TextInput
                label='رقم الهاتف'
                icon={mobile}
                placeholder='أدخل رقم الهاتف'
                name='mobile'
                value={values.mobile}
                onChange={(name, value) => setFieldValue(name, value)}
                errors={errors.mobile}
                touched={touched.mobile}
                required
                left
              />

              <TextInput
                label='اسم المستخدم'
                icon={usericon}
                placeholder='أدخل اسم المستخدم'
                name='username'
                value={values.username}
                onChange={(name, value) => setFieldValue(name, value)}
                errors={errors.username || errorMessage}
                touched={touched.username || errorMessage}
                required
                left
              />

              {isAdd && (
                <TextInput
                  label='كلمة المرور'
                  icon={emailicon}
                  placeholder='أدخل كلمة المرور'
                  type='password'
                  name='password'
                  value={values.password}
                  onChange={(name, value) => setFieldValue(name, value)}
                  errors={errors.password}
                  touched={touched.password}
                  required
                  left
                  info='يجب أن تحتوي كلمة المرور على 8 إلى 15 حرفًا، بما في ذلك حرف واحد كبير، حرف واحد صغير، رقم واحد، ورمز خاص واحد.'
                />
              )}

              {/* The same tree as «إعادة تعيين الصفحة» — same rows, same
                  grouping, same behaviour — now on create AND on edit. */}
              <PagesTree
                label='تعيين الصفحات'
                pagesData={pagesData}
                selected={selectedPages}
                onChange={setSelectedPages}
              />

              {isAddingUser ? (
                <LoaderButton />
              ) : (
                <Buttons
                  text={isAdd ? 'حفظ' : 'تحديث'}
                  handleOnClick={submit}
                  rounded
                />
              )}
            </ModalChildrenContainer>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default UserModal;
